import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_API_URL || "http://localhost:8080";
const LIMIT = 6;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setLoading(true);
    fetch(`${API}/?url=blog/posts&page=${page}&limit=${LIMIT}&_t=${Date.now()}`)
      .then((r) => r.json())
      .then((data) => {
        setPosts(data.posts || []);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  const goTo = (p) => {
    setSearchParams({ page: p });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      <Helmet>
        <title>Blog &amp; News | Clear &amp; Clean Christchurch</title>
        <meta name="description" content="Cleaning tips, industry insights and news from Clear & Clean — Christchurch's commercial cleaning experts." />
        <meta name="robots" content={!loading && total === 0 ? "noindex, follow" : "index, follow"} />
        <link rel="canonical" href={`https://www.clearandclean.co.nz/blog${page > 1 ? `?page=${page}` : ""}`} />
      </Helmet>

      {/* Hero */}
      <section className="hero-wrap hero-wrap-2" style={{ backgroundImage: 'url("/images/service_2.jpg")' }} data-stellar-background-ratio="0.5">
        <div className="overlay" />
        <div className="container">
          <div className="row no-gutters slider-text align-items-end">
            <div className="col-md-9 pb-5">
              <p className="breadcrumbs mb-2">
                <span className="mr-2"><Link to="/">Home <i className="fa fa-chevron-right" /></Link></span>
                <span>Blog <i className="fa fa-chevron-right" /></span>
              </p>
              <h1 className="mb-0 bread">Blog &amp; News</h1>
            </div>
          </div>
        </div>
      </section>

      <section className="ftco-section" style={{ background: "#f9fafb" }}>
        <div className="container">
          <div className="row justify-content-center pb-4">
            <div className="col-md-8 text-center">
              <span className="subheading">Insights</span>
              <h2>Cleaning Tips &amp; Industry News</h2>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-secondary" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center text-muted py-5">No posts published yet. Check back soon!</p>
          ) : (
            <>
              <div className="row">
                {posts.map((post) => (
                  <div key={post.id} className="col-md-6 col-lg-4 mb-4">
                    <div
                      style={{
                        background: "#fff", borderRadius: 12, overflow: "hidden",
                        boxShadow: "0 2px 12px rgba(0,0,0,.06)", height: "100%",
                        display: "flex", flexDirection: "column",
                        transition: "box-shadow .2s, transform .2s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,.12)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,.06)"; e.currentTarget.style.transform = "translateY(0)"; }}
                    >
                      {post.featured_image ? (
                        <div style={{ height: 200, overflow: "hidden" }}>
                          <img src={post.featured_image} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                      ) : (
                        <div style={{ height: 200, background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className="fa fa-pencil-square-o" style={{ fontSize: 48, color: "#444" }} />
                        </div>
                      )}
                      <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column" }}>
                        <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>
                          <i className="fa fa-user mr-1" />{post.author}&nbsp;·&nbsp;
                          <i className="fa fa-calendar mr-1" />{formatDate(post.created_at)}
                        </div>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a1a", marginBottom: 10, lineHeight: 1.4 }}>
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p style={{ color: "#666", fontSize: 14, lineHeight: 1.7, flex: 1, margin: "0 0 16px" }}>
                            {post.excerpt.length > 120 ? post.excerpt.slice(0, 117) + "…" : post.excerpt}
                          </p>
                        )}
                        <Link to={`/blog/${post.slug}`} className="btn btn-dark btn-sm" style={{ alignSelf: "flex-start", borderRadius: 6 }}>
                          Read More <i className="fa fa-arrow-right ml-1" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="row justify-content-center mt-4">
                  <div className="col-auto text-center">
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        onClick={() => goTo(page - 1)}
                        disabled={page === 1}
                        style={pgBtn(page === 1)}
                      >
                        <i className="fa fa-chevron-left" />
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          onClick={() => goTo(p)}
                          style={pgBtn(false, p === page)}
                        >
                          {p}
                        </button>
                      ))}

                      <button
                        onClick={() => goTo(page + 1)}
                        disabled={page === totalPages}
                        style={pgBtn(page === totalPages)}
                      >
                        <i className="fa fa-chevron-right" />
                      </button>
                    </div>
                    <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 10, marginBottom: 0 }}>
                      Page {page} of {totalPages} &nbsp;·&nbsp; {total} article{total !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

const pgBtn = (disabled = false, active = false) => ({
  padding: "8px 14px", borderRadius: 8,
  border: `1px solid ${active ? "#1a1a1a" : "#d1d5db"}`,
  background: active ? "#1a1a1a" : disabled ? "#f3f4f6" : "#fff",
  color: active ? "#fff" : disabled ? "#9ca3af" : "#374151",
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize: 13, fontWeight: active ? 700 : 400, minWidth: 38,
});

export default Blog;
